import { useEffect, useState } from "react";
import {
  AdminButton,
  AdminSection,
  HourInput,
  Notice,
} from "./AdminPrimitives";
import { useResetThresholds, useUpdateThresholds } from "../../hooks/useAdmin";
import type { ThresholdsDto } from "../../types/admin";

/** 편집 폼의 필드 정의 — 라벨/단위/설명을 한 곳에서 관리한다. */
const FIELDS: {
  key: keyof ThresholdsDto;
  label: string;
  unit: string;
  hint: string;
  min: number;
  max: number;
}[] = [
  {
    key: "downAfterHoursLarge",
    label: "평일 주간 · 대",
    unit: "시간",
    hint: "면수 기준 이상인 층",
    min: 1,
    max: 8760,
  },
  {
    key: "downAfterHoursSmall",
    label: "평일 주간 · 소",
    unit: "시간",
    hint: "면수 기준 미만인 층",
    min: 1,
    max: 8760,
  },
  {
    key: "downAfterHoursNight",
    label: "야간",
    unit: "시간",
    hint: "요일·면수 무관 (최우선)",
    min: 1,
    max: 8760,
  },
  {
    key: "downAfterHoursWeekend",
    label: "주말 주간",
    unit: "시간",
    hint: "토·일, 면수 무관",
    min: 1,
    max: 8760,
  },
  {
    key: "spaceThreshold",
    label: "대/소 분기 면수",
    unit: "면",
    hint: "이 값 이상이면 '대'",
    min: 0,
    max: 100000,
  },
  {
    key: "nightStartHour",
    label: "야간 시작",
    unit: "시",
    hint: "포함 (0~23)",
    min: 0,
    max: 23,
  },
  {
    key: "nightEndHour",
    label: "야간 종료",
    unit: "시",
    hint: "미포함 (0~23)",
    min: 0,
    max: 23,
  },
  {
    key: "staleAfterDays",
    label: "미운영 판정",
    unit: "일",
    hint: "이 기간 넘으면 STALE",
    min: 1,
    max: 3650,
  },
];

function isSame(a: ThresholdsDto, b: ThresholdsDto): boolean {
  return FIELDS.every((f) => a[f.key] === b[f.key]);
}

/**
 * 전역 기본 임계값 편집.
 *
 * 저장하면 재배포·재기동 없이 다음 폴링부터 적용된다. 주차장별 예외가 걸린 lot 은
 * 그 예외가 계속 우선한다.
 */
export default function GlobalThresholdPanel({
  token,
  current,
  defaults,
}: {
  token: string | null;
  current: ThresholdsDto;
  defaults: ThresholdsDto;
}) {
  const [draft, setDraft] = useState<ThresholdsDto>(current);
  const [saved, setSaved] = useState(false);
  const update = useUpdateThresholds(token);
  const reset = useResetThresholds(token);

  // 서버 값이 바뀌면(저장 성공/되돌리기) 폼을 그 값으로 다시 맞춘다.
  useEffect(() => {
    setDraft(current);
  }, [current]);

  const dirty = !isSame(draft, current);
  const atDefaults = isSame(current, defaults);
  const pending = update.isPending || reset.isPending;
  const error = update.error ?? reset.error;

  return (
    <AdminSection
      title="전역 기본 임계값"
      description="마지막 데이터 수신 후 이 시간이 지나면 '데이터 정체(DOWN)'로 판정합니다. 야간이 가장 우선하고, 그다음 주말, 마지막으로 평일 주간이 면수에 따라 갈립니다."
      action={
        <AdminButton
          disabled={pending || atDefaults}
          onClick={() => {
            setSaved(false);
            reset.mutate(undefined, { onSuccess: () => setSaved(true) });
          }}
        >
          {atDefaults ? "기본값 적용 중" : "기본값으로 되돌리기"}
        </AdminButton>
      }
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {FIELDS.map((f) => (
          <HourInput
            key={f.key}
            label={`${f.label} (${f.unit})`}
            hint={f.hint}
            min={f.min}
            max={f.max}
            value={draft[f.key]}
            onChange={(v) =>
              setDraft((d) => ({ ...d, [f.key]: v ?? 0 }))
            }
          />
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {error && <Notice tone="error">{error.message}</Notice>}
        {saved && !dirty && !error && (
          <Notice tone="success">저장했습니다. 다음 폴링부터 적용됩니다.</Notice>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <AdminButton
            tone="primary"
            disabled={!dirty || pending}
            onClick={() => {
              setSaved(false);
              update.mutate(draft, { onSuccess: () => setSaved(true) });
            }}
          >
            {update.isPending ? "저장 중…" : "저장"}
          </AdminButton>
          {dirty && (
            <AdminButton disabled={pending} onClick={() => setDraft(current)}>
              변경 취소
            </AdminButton>
          )}
          <span className="text-[11px] text-slate-400 dark:text-slate-500">
            yml 기본값: {defaults.downAfterHoursLarge} / {defaults.downAfterHoursSmall} /{" "}
            {defaults.downAfterHoursNight} / {defaults.downAfterHoursWeekend}시간 ·{" "}
            {defaults.spaceThreshold}면 · 야간 {defaults.nightStartHour}~
            {defaults.nightEndHour}시 · 미운영 {defaults.staleAfterDays}일
          </span>
        </div>
      </div>
    </AdminSection>
  );
}
